import React, { useState, useEffect, useRef } from 'react';

function Window({
    id,
    title,
    isOpen,
    isMinimized,
    isMaximized,
    onClose,
    onMinimize,
    onMaximize,
    activeWindowId,
    setActiveWindow,
    defaultPosition = { x: 120, y: 80 },
    defaultSize = { width: 620, height: 420 },
    children
}) {
    const [position, setPosition] = useState(defaultPosition);
    const windowRef = useRef(null);
    const isDragging = useRef(false);
    const dragStart = useRef({ x: 0, y: 0 });
    const positionStart = useRef({ x: 0, y: 0 });

    // Set a sensible starting position based on screen width
    useEffect(() => {
        const workspaceWidth = window.innerWidth;
        const workspaceHeight = window.innerHeight;
        
        // Distribute default positions based on window index
        let multiplier = 1;
        if (id === 'projects') multiplier = 1.8;
        if (id === 'contact') multiplier = 2.6;
        if (id === 'terminal') multiplier = 3.4;

        const defaultX = Math.max(20, Math.min(50 * multiplier + 60, workspaceWidth - 650));
        const defaultY = Math.max(50, Math.min(40 * multiplier + 40, workspaceHeight - 480));
        
        setPosition({ x: defaultX, y: defaultY });
    }, [id]);

    const handlePointerDown = (e) => {
        // Bring window to front
        setActiveWindow(id);
        
        // Ignore dragging if clicking a control button
        if (e.target.closest('.window-dot')) return;
        if (isMaximized) return;

        isDragging.current = true;
        dragStart.current = { x: e.clientX, y: e.clientY };
        positionStart.current = { ...position };
        
        // Set capture to receive events even if mouse moves outside window boundary
        e.target.setPointerCapture(e.pointerId);
    };

    const handlePointerMove = (e) => {
        if (!isDragging.current) return;
        
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        
        const workspaceWidth = window.innerWidth;
        const workspaceHeight = window.innerHeight;
        
        let newX = positionStart.current.x + dx;
        let newY = positionStart.current.y + dy;
        
        // Keep header accessible
        newY = Math.max(36, Math.min(newY, workspaceHeight - 50));
        newX = Math.max(-200, Math.min(newX, workspaceWidth - 100));
        
        setPosition({ x: newX, y: newY });
    };

    const handlePointerUp = (e) => {
        if (isDragging.current) {
            isDragging.current = false;
            try {
                e.target.releasePointerCapture(e.pointerId);
            } catch (err) {
                // Ignore error if element is unmounted
            }
        }
    };

    if (!isOpen) return null;

    const isActive = activeWindowId === id;

    const style = isMaximized 
        ? {
            top: '36px',
            left: 0,
            width: '100%',
            height: 'calc(100vh - 120px)',
            zIndex: isActive ? 100 : 10,
          }
        : {
            top: `${position.y}px`,
            left: `${position.x}px`,
            width: `${defaultSize.width}px`,
            height: `${defaultSize.height}px`,
            zIndex: isActive ? 100 : 10,
            transform: isMinimized ? 'scale(0.8) translateY(100px)' : 'scale(1) translateY(0)',
            opacity: isMinimized ? 0 : 1,
            pointerEvents: isMinimized ? 'none' : 'auto'
          };

    return (
        <div
            ref={windowRef}
            className={`window ${isActive ? 'active' : ''}`}
            style={style}
            onClick={() => setActiveWindow(id)}
        >
            <div 
                className="window-header" 
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
            >
                <div className="window-controls">
                    <button className="window-dot close" onClick={(e) => { e.stopPropagation(); onClose(id); }} aria-label="Close"></button>
                    <button className="window-dot minimize" onClick={(e) => { e.stopPropagation(); onMinimize(id); }} aria-label="Minimize"></button>
                    <button className="window-dot maximize" onClick={(e) => { e.stopPropagation(); onMaximize(id); }} aria-label="Maximize"></button>
                </div>
                <div className="window-title">{title}</div>
                <div style={{ width: '52px' }}></div>
            </div>
            <div className="window-body">
                <div className="window-content">
                    {children}
                </div>
            </div>
        </div>
    );
}

export default Window;
